// @ts-strict-ignore
import {ParamComponent} from 'panel';

import {ValidationStatus} from 'common/components/input/types';
import {parseNumber} from 'common/util';
import {SECONDS_BY_UNIT} from 'common/util/date';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {IronContextRegistry} from 'common/report/context-registry';

import {BucketType} from '../types';
import {getBucketPreview, getBucketType} from './util';
import {template} from './template';
import css from './index.inline.styl';

interface NumericBucketPaneState {
  currentTab: BucketType;
  hasSelectedDefaults: boolean;
  editedUnit: TimeUnit;
  tooltipErrorMsg: string;
}

export interface NumericBucketPaneParams {
  units?: string[] | Array<{value: string; label: string}>;
  useNewDesign: boolean;
  selectedBucket?: Bucket;
  selectedUnit?: string;
  resetBucket?: boolean;
  maxNumBuckets?: number;
  rangeMin?: number;
  header?: string;
  unitsAsProperty?: boolean;
  onUpdate?: (Bucket) => void;
  onKeyup?: (ev: any) => void;
}

interface NumericBucketPaneJsxProps {
  params: NumericBucketPaneParams;
}

function firstValidationError(validationFns: Array<() => string>): string {
  for (const validationFn of validationFns) {
    const errorMessage = validationFn();
    if (errorMessage) {
      return errorMessage;
    }
  }
  return null;
}

/** widget for selecting bucket size, min, and max. */
export class NumericBucketPane extends ParamComponent<
  NumericBucketPaneParams,
  NumericBucketPaneState,
  IronContextRegistry
> {
  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`i18n` as const],
      defaultState: {
        currentTab: BucketType.Even,
        editedUnit: null,
        hasSelectedDefaults: false,
        tooltipErrorMsg: null,
      },
      defaultParams: {
        maxNumBuckets: 90,
        rangeMin: null,
        header: `Define Range`,
      },
      params: {
        units: Array,
        useNewDesign: Boolean,
        selectedBucket: Object,
        selectedUnit: String,
        maxNumBuckets: Number,
        rangeMin: Number,
        header: String,
        unitsAsProperty: Boolean,
        onUpdate: Function,
      },
      helpers: {
        changeBucketSize: (ev) => {
          const editedBucket = {
            ...this.params.selectedBucket,
            bucketSize: this.helpers.selectedUnitToVal(parseNumber(ev.target.value), this.unit),
          };
          const errorMsg = this.validate(editedBucket);
          this.update({tooltipErrorMsg: errorMsg});
          this.emitUpdate({editedBucket});
        },
        changeBucketUnit: (ev) => {
          const {value: newUnit, isSelected} = ev.detail.item;
          if (!isSelected) {
            const prevUnit = this.unit;
            this.update({editedUnit: newUnit});
            const editedBucket = {
              ...this.params.selectedBucket,
              bucketSize: this.convertUnit(this.params.selectedBucket.bucketSize, prevUnit, this.unit),
              max: this.convertUnit(this.params.selectedBucket.max, prevUnit, this.unit),
              min: this.convertUnit(this.params.selectedBucket.min, prevUnit, this.unit),
            };
            this.emitUpdate({editedBucket});
          }
        },
        changeBucketGroup: (ev, index) => {
          const editedBucket = this.params.selectedBucket;
          const groups = [...editedBucket.groups];
          if (ev.target.value === ``) {
            groups.splice(index, 1);
          } else {
            groups[index] = parseNumber(ev.target.value);
          }
          this.emitUpdate({editedBucket: {...editedBucket, groups}});
        },
        changeBucketMax: (ev) => {
          const editedBucket = {
            ...this.params.selectedBucket,
            max: this.helpers.selectedUnitToVal(parseNumber(ev.target.value), this.unit),
          };
          const errorMsg = this.validate(editedBucket);
          this.update({tooltipErrorMsg: errorMsg});
          this.emitUpdate({editedBucket});
        },
        changeBucketMin: (ev) => {
          const editedBucket = {
            ...this.params.selectedBucket,
            min: this.helpers.selectedUnitToVal(parseNumber(ev.target.value), this.unit),
          };
          const errorMsg = this.validate(editedBucket);
          this.update({tooltipErrorMsg: errorMsg});
          this.emitUpdate({editedBucket});
        },
        selectDefault: (vnode) =>
          !this.state.hasSelectedDefaults &&
          requestAnimationFrame(() => {
            vnode.elm.select();
            this.update({hasSelectedDefaults: true});
          }),
        canSelectBucketUnit: () => {
          return this.params.units && this.params.units.length > 1;
        },
        getBucketUnits: () => {
          const units = this.helpers.canSelectBucketUnit() ? this.params.units : [this.params[`selected-unit`]];
          return [
            {
              items: units.map((unit) => {
                const value = typeof unit === `string` ? unit : unit.value;
                const label = typeof unit === `string` ? unit : unit.label;

                return {
                  value,
                  label,
                  isSelected: unit === this.unit,
                };
              }),
            },
          ];
        },
        getBucketSize: () => {
          return this.helpers.valToSelectedUnit(this.params.selectedBucket.bucketSize, this.unit);
        },
        getMax: () => {
          return this.helpers.valToSelectedUnit(this.params.selectedBucket.max, this.unit);
        },
        getMin: () => {
          return this.helpers.valToSelectedUnit(this.params.selectedBucket.min, this.unit);
        },
        getInputValidationStatus: (errorMessage: string) => {
          return errorMessage ? ValidationStatus.Invalid : ValidationStatus.Default;
        },
        getBucketSizeValidationStatus: () => {
          return this.helpers.getInputValidationStatus(this.validateBucketSize(this.helpers.getBucketSize()));
        },
        getMaxValidationStatus: () => {
          return this.helpers.getInputValidationStatus(this.validateMax(this.helpers.getMax()));
        },
        getMinValidationStatus: () => {
          return this.helpers.getInputValidationStatus(this.validateMin(this.helpers.getMin()));
        },
        /** Transforms if unit is in default transform eg Minutes, Hours, Days */
        valToSelectedUnit: (val: number, unit: string) => {
          if (this.params.unitsAsProperty || !Object.prototype.hasOwnProperty.call(SECONDS_BY_UNIT, unit)) {
            return val;
          }
          return val / SECONDS_BY_UNIT[unit];
        },
        selectedUnitToVal: (unitVal: number, unit: string) => {
          if (this.params.unitsAsProperty || !Object.prototype.hasOwnProperty.call(SECONDS_BY_UNIT, unit)) {
            return unitVal;
          }
          return unitVal * SECONDS_BY_UNIT[unit];
        },
        submitDisabled: () => {
          return Boolean(this.state.tooltipErrorMsg);
        },
        getFilledUnevenBuckets: (bucket: Bucket) => (bucket.groups || []).filter((group) => group !== null),
        getUnevenBuckets: () => {
          const buckets = this.helpers.getFilledUnevenBuckets(this.params.selectedBucket);

          if (!this.state.hasSelectedDefaults) {
            return [this.helpers.getMin(), null];
          }

          return buckets.length < this.params.maxNumBuckets ? [...buckets, null] : buckets;
        },
        getSortedUnevenBuckets: () =>
          this.helpers.getFilledUnevenBuckets(this.params.selectedBucket).sort((a, b) => a - b),
        handleTabClick: (ev) => {
          this.update({currentTab: ev.detail.selected, hasSelectedDefaults: false});
          let update = {};
          // set a default update. TODO can remove with autobucketing potentially
          if (this.state.currentTab === BucketType.Varied && !this.params.selectedBucket.groups?.length) {
            update = {editedBucket: {...this.params.selectedBucket, groups: [this.params.selectedBucket?.min ?? 0]}};
          }
          this.emitUpdate(update);
        },
        sortUnevenBuckets: () => {
          this.emitUpdate({
            editedBucket: {...this.params.selectedBucket, groups: this.helpers.getSortedUnevenBuckets()},
          });
        },
      },
    };
  }
  connectedCallback() {
    super.connectedCallback();
    // Could in theory get rid of this state and always calculate this via a helper
    this.update({currentTab: getBucketType(this.params.selectedBucket)});
  }

  get unit() {
    return this.state.editedUnit || this.params.selectedUnit;
  }

  convertUnit(num: number, firstUnit: string, secondUnit: string) {
    if (this.params.unitsAsProperty) {
      return num;
    }
    return this.helpers.selectedUnitToVal(this.helpers.valToSelectedUnit(num, firstUnit), secondUnit);
  }

  getBucketForDispatch(editedBucket: Bucket) {
    const groups = (editedBucket.groups || []).filter((group) => group !== null);
    return {
      bucketSize: editedBucket.bucketSize,
      max: editedBucket.max,
      min: editedBucket.min,
      offset: editedBucket.offset,
      disabled: this.state.currentTab === BucketType.None,
      groups: this.state.currentTab === BucketType.Varied ? groups : null,
      unit: this.params.unitsAsProperty ? this.unit : null,
    };
  }

  getBucketPreview() {
    return getBucketPreview(this.params.selectedBucket);
  }

  emitUpdate({editedBucket}: {editedBucket?: Bucket}) {
    const newBucket = editedBucket || this.params.selectedBucket;
    this.params.onUpdate({
      bucket: this.getBucketForDispatch(newBucket),
      isValid: !this.validate(newBucket),
    });
  }

  validateNumber({
    fieldName,
    number,
    inclusiveMin,
    exclusiveMin,
  }: {
    fieldName: string;
    number: number;
    inclusiveMin?: number;
    exclusiveMin?: number;
  }): string {
    return firstValidationError([
      () => (!Number.isFinite(number) ? `${fieldName} is not specified` : null),
      () => (Number.isFinite(inclusiveMin) && number < inclusiveMin ? `${fieldName} must be >= ${inclusiveMin}` : null),
      () => (Number.isFinite(exclusiveMin) && number <= exclusiveMin ? `${fieldName} must be > ${exclusiveMin}` : null),
    ]);
  }

  validateBucketSize(bucketSize: number): string {
    return this.validateNumber({fieldName: `Interval Size`, number: bucketSize, exclusiveMin: 0});
  }

  validateMax(max: number): string {
    return this.validateNumber({fieldName: `Maximum`, number: max});
  }

  validateMin(min: number): string {
    return this.validateNumber({fieldName: `Minimum`, number: min, inclusiveMin: this.params[`range-min`]});
  }

  validateUnevenBucket(bucket: Bucket): string {
    const unevenBuckets = this.helpers.getFilledUnevenBuckets(bucket);
    if (unevenBuckets.length === 0) {
      return `Must contain at least one value`;
    } else if (unevenBuckets.length !== new Set(unevenBuckets).size) {
      return `Cannot contain duplicate values`;
    } else if (unevenBuckets.some((b) => !Number.isFinite(b))) {
      return `All values must be valid numbers`;
    }
    return null;
  }

  validate(bucket: Bucket): string {
    const bucketDifference = () => bucket.max - bucket.min;
    switch (this.state.currentTab) {
      case BucketType.Even:
        return firstValidationError([
          () => this.validateBucketSize(bucket.bucketSize),
          () => this.validateMin(bucket.min),
          () => this.validateMax(bucket.max),
          () => (bucketDifference() < 0 ? `Invalid interval: Max must be greater than Min` : null),
          () =>
            bucketDifference() / bucket.bucketSize > this.params.maxNumBuckets
              ? `Invalid interval: Number of intervals cannot exceed ${this.params.maxNumBuckets}`
              : null,
          () =>
            bucket.bucketSize > bucketDifference()
              ? `Invalid interval: interval cannot be greater than the given range`
              : null,
        ]);
      case BucketType.Varied:
        return this.validateUnevenBucket(bucket);
      case BucketType.None:
        return null;
    }
  }
}

export const NumericBucketPaneEl = defineAndMakeJsxFactory<NumericBucketPaneJsxProps>(
  `mp-numeric-bucket-input`,
  NumericBucketPane,
);
export const NumericBucketPaneReact = panel2react<NumericBucketPaneJsxProps>(
  `mp-numeric-bucket-input`,
  NumericBucketPane,
);
